import { keepPreviousData, useQuery } from '@tanstack/react-query';
import type { FundNameMatch } from '@nemo/api';
import { useEffect, useState } from 'react';
import { useTRPC } from '../trpc.js';

const SEARCH_DEBOUNCE_MS = 200;

export interface FundNameSearch {
  /** What the last answer found, which may be for slightly older text until `settled`. */
  readonly matches: readonly FundNameMatch[];
  /** The matches are for the text as it now stands (or the search failed, and there are none). */
  readonly settled: boolean;
}

/** The funds a picker's text could mean, asked of the server as it is typed. */
export function useFundNameSearch(
  text: string,
  options: { limit?: number; notOnList?: string } = {},
): FundNameSearch {
  const trpc = useTRPC();
  const typed = text.trim();
  const [asked, setAsked] = useState(typed);
  useEffect(() => {
    const timer = window.setTimeout(() => setAsked(typed), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [typed]);

  const answer = useQuery(
    trpc.funds.search.queryOptions(
      { text: asked, ...options },
      { enabled: asked !== '', placeholderData: keepPreviousData },
    ),
  );

  if (typed === '') return { matches: [], settled: true };
  return {
    matches: answer.data ?? [],
    settled: asked === typed && !answer.isPending && !answer.isPlaceholderData,
  };
}
